import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loading } from '@/ui/components/atoms/Loading';
import { ControlsContainer } from '@/ui/components/molecules/ControlsContainer';
import { ExpertiseList } from '@/ui/components/molecules/ExpertiseList';
import { useATraiter } from '../../hooks/useATraiter';
import type { ATraiterViewProps } from '../../types/a-traiter.types';
import './ATraiterView.styles.css';

export const ATraiterView: React.FC<ATraiterViewProps> = ({ 
  navigationParams 
}) => {
  const navigate = useNavigate();
  const {
    expertises,
    expertName,
    stats,
    isLoading,
    error,
    filters,
    updateFilter,
  } = useATraiter(navigationParams?.filter);

  // Update filter when navigation params change
  useEffect(() => {
    if (navigationParams?.filter === 'urgent') {
      updateFilter('activeFilter', 'urgent');
    } else if (navigationParams?.filter === undefined && filters.activeFilter === 'urgent') {
      updateFilter('activeFilter', 'all');
    }
  }, [navigationParams]);

  const handleExpertiseClick = (expertiseId: number | string) => {
    navigate(`/expert/affaire/${expertiseId}`, {
      state: { from: '/expert/a-traiter' }
    });
  };

  if (isLoading) {
    return (
      <div className="a-traiter-container">
        <Loading 
          size="large" 
          text="Chargement des expertises..."
          fullScreen={false}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="a-traiter-container">
        <div className="error-message">
          {error instanceof Error ? error.message : 'Une erreur est survenue'}
        </div>
      </div>
    );
  }

  // Transform expertises data for ExpertiseList to match ExpertiseCardProps
  const expertiseListData = expertises.map(expertise => {
    const tags = [];
    
    // Add days since RV tag if urgent
    if (expertise.isUrgent && expertise.urgencyReason) {
      tags.push(expertise.urgencyReason);
    } else if (expertise.daysSinceRv !== null) {
      tags.push(`${expertise.daysSinceRv} jour${expertise.daysSinceRv !== 1 ? 's' : ''} depuis RV`);
    }

    // Add payment status tag
    if (expertise.perception) {
      if (expertise.perception === 'PAYE BANQUE' || 
          expertise.perception?.toLowerCase().includes('preuve virement')) {
        tags.push('Payé');
      } else if (expertise.perception?.toLowerCase().includes('facturer')) {
        tags.push('Facturé');
      } else if (expertise.perception === 'En attente') {
        tags.push('Attente paiement');
      } else {
        tags.push(expertise.perception);
      }
    }
    
    // Add invoice status if relevant
    if (expertise.facture_emise === 'oui' || expertise.facture_emise === 'emise') {
      tags.push('Facture émise');
    }

    // Build full client name with contact info for display
    let clientDisplay = expertise.client?.name || 'Client non renseigné';
    if (expertise.client?.phone) {
      clientDisplay += ` - ${expertise.client.phone}`;
    }
    if (expertise.client?.email) {
      clientDisplay += ` - ${expertise.client.email}`;
    }

    // Map to ExpertiseCardProps interface
    return {
      // Core data (required)
      id: String(expertise.id),
      reference: String(expertise.id),
      title: expertise.type || 'Expertise immobilière',
      status: 'en_cours' as const, // Map to valid ExpertiseStatus
      statusDisplay: expertise.status || 'status not found', // Use actual backend status

      // Display variant
      variant: 'standard' as const,
      
      // Common fields
      clientName: clientDisplay,
      expertName: expertise.expert || expertName || 'Non assigné',
      createdAt: expertise.date || undefined,
      updatedAt: expertise.date_du_rv || undefined,
      dueDate: expertise.date_du_rv || undefined,
      
      // Address information
      address: expertise.address ? {
        street: expertise.address.join(', '),
        city: undefined,
        postalCode: undefined,
        country: 'France'
      } : undefined,
      
      // Financial information
      amount: typeof expertise.price === 'string' ? parseFloat(expertise.price) : expertise.price,
      currency: 'EUR',
      paid: expertise.perception === 'PAYE BANQUE' || 
            expertise.perception?.toLowerCase().includes('preuve virement'),
      perception: expertise.perception,
      invoiceStatus: expertise.affaire_facturee === 'oui' ? 'emise' : undefined,
      invoiceIssued: expertise.facture_emise === 'oui' || expertise.facture_emise === 'emise',
      
      // Progress information
      progress: 65, // À achever is typically at 65%
      stage: expertise.status || 'À achever',
      stageId: 0,
      priority: expertise.isUrgent ? 'urgent' as const : 'medium' as const,
      
      // Tags and categories
      tags,
      category: 'a-traiter',
      type: expertise.type || 'Expertise immobilière',
      
      // Description and notes  
      description: expertise.heure_rv ? `RV à ${expertise.heure_rv.substring(0, 5)}` : undefined,
      notes: expertise.timeSince || undefined,
      
      // Statistics
      documentsCount: undefined,
      messagesCount: undefined,
      tasksCompleted: undefined,
      tasksPending: undefined,
      
      // UI customization
      className: expertise.isUrgent ? 'urgent-expertise' : undefined,
      showActions: true,
      selectable: false,
      selected: false,
      disabled: false,
      loading: false
    };
  });

  return (
    <div className="a-traiter-page">
      {/* Header with title and stats - matching Planning style */}
      <div className="a-traiter-page__header">
        <div className="a-traiter-page__header-content">
          <h1 className="a-traiter-page__title">
            À traiter
          </h1>
          <p className="a-traiter-page__subtitle">
            Gérez vos expertises à achever et visites à planifier
          </p>
        </div>
        
        <div className="a-traiter-page__actions">
          {stats && (
            <>
              <span className="a-traiter-stat">
                À visiter: <strong>{stats.a_visiter}</strong>
              </span>
              <span className="a-traiter-stat">
                À achever: <strong>{stats.a_achever}</strong>
              </span>
              <span className="a-traiter-stat">
                Planifiées: <strong>{stats.visites_planifiees}</strong>
              </span>
            </>
          )}
        </div>
      </div>

      {/* Search and controls bar */}
      <div className="a-traiter-page__controls">
        <ControlsContainer
          searchQuery={filters.searchQuery}
          onSearchChange={(value) => updateFilter('searchQuery', value)}
          sortBy={filters.sortBy}
          onSortChange={(value) => updateFilter('sortBy', value as 'date' | 'date_rv' | 'id')}
          sortOrder={filters.sortOrder}
          onSortOrderChange={(value) => updateFilter('sortOrder', value)}
          sortOptions={[
            { value: 'date', label: 'Date demande' },
            { value: 'date_rv', label: 'Date visite' },
            { value: 'id', label: 'Numéro' }
          ]}
          activeFilter={filters.activeFilter}
          onFilterChange={(value) => updateFilter('activeFilter', value as 'all' | 'urgent' | 'paid' | 'unpaid')}
          filterOptions={[
            { key: 'all', label: 'Toutes' },
            { key: 'urgent', label: 'Urgent' },
            { key: 'paid', label: 'Payé' },
            { key: 'unpaid', label: 'Non payé' }
          ]}
          showFilters={true}
          resultsCount={expertises.length}
          searchPlaceholder="Rechercher par adresse, client, numéro..."
          className="a-traiter-controls"
        />
      </div>

      {/* Expertise list */}
      <div className="a-traiter-page__content">
        <ExpertiseList
        expertises={expertiseListData}
        onExpertiseClick={(expertise) => handleExpertiseClick(expertise.id)}
        showFacadeImage={true}
        showProgress={true}
        showActions={true}
        emptyStateTitle="Aucune expertise à traiter"
        emptyStateMessage={
          filters.searchQuery || filters.activeFilter !== 'all'
            ? 'Aucune expertise ne correspond aux critères de recherche'
            : 'Aucune expertise à traiter pour le moment'
        }
        isSearchActive={!!filters.searchQuery || filters.activeFilter !== 'all'}
          className="a-traiter-list"
        />
      </div>
    </div>
  );
};